import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CustomerApiError, customerApi } from '../customer_api';
import { formatMoney } from '../lib/format';

/**
 * PayBookingPanel — Stripe.js Payment Element wrapper for a single
 * booking payment (Plan 3 M7).
 *
 * Flow:
 *   1. Initial state: renders a "Pay" button with the price.
 *   2. Click "Pay" → POST /api/customer/bookings/:id/pay → returns
 *      `{ client_secret, stripe_account_id, amount_minor, currency, kind }`.
 *   3. `loadStripe(publishableKey, { stripeAccount })` + mount the
 *      PaymentElement against an empty div ref.
 *   4. User fills the form + clicks "Pay now" → `stripe.confirmPayment`
 *      with `redirect: 'if_required'`. SCA / 3DS is handled by
 *      Stripe.js internally.
 *   5. On success → `onPaid()`. On error → inline message + `onError()`.
 *
 * The publishable key is the platform's (pk_test/pk_live), passed in
 * via prop — the parent reads `import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY`
 * once and passes it through. The connected account id is returned by
 * the POST /pay response (the server already validated Connect is
 * charges-enabled). This means a single client_secret round-trip per
 * payment attempt.
 */

interface Props {
  bookingId: string;
  /**
   * Platform publishable key (pk_test_... / pk_live_...). Safe to ship
   * to the browser.
   */
  publishableKey: string;
  /** Called after `stripe.confirmPayment` resolves successfully. */
  onPaid: () => void;
  /** Optional: called with a human-readable error message on failure. */
  onError?: (message: string) => void;
  /** Optional discount code applied when creating the PaymentIntent. */
  discountCode?: string;
}

interface PayResponse {
  client_secret: string;
  payment_intent_id: string;
  stripe_account_id: string;
  kind: 'full' | 'deposit' | 'balance';
  amount_minor: number;
  currency: string;
}

type Phase = 'idle' | 'creating_intent' | 'element_ready' | 'submitting' | 'paid' | 'error';

export function PayBookingPanel({
  bookingId,
  publishableKey,
  onPaid,
  onError,
  discountCode,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [intent, setIntent] = useState<PayResponse | null>(null);

  // Hold the live Stripe + Elements instances so the submit handler
  // can call confirmPayment without re-creating them.
  const stripeRef = useRef<Stripe | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: StripeElements isn't worth re-typing here.
  const elementsRef = useRef<any>(null);

  // The DOM node the PaymentElement mounts into.
  const mountNodeRef = useRef<HTMLDivElement | null>(null);

  const fail = useCallback(
    (msg: string) => {
      setPhase('error');
      setErrorMsg(msg);
      onError?.(msg);
    },
    [onError],
  );

  // Step 1+2+3: create intent + load Stripe + mount Element.
  const beginPayment = useCallback(async () => {
    if (phase === 'creating_intent' || phase === 'submitting') return;
    setPhase('creating_intent');
    setErrorMsg(null);

    let payResponse: PayResponse;
    try {
      payResponse = await customerApi<PayResponse>(`/api/customer/bookings/${bookingId}/pay`, {
        method: 'POST',
        body: JSON.stringify(discountCode ? { discountCode } : {}),
      });
    } catch (err) {
      const msg =
        err instanceof CustomerApiError
          ? `Could not start payment (HTTP ${err.status}).`
          : 'Could not start payment. Please try again.';
      fail(msg);
      return;
    }

    setIntent(payResponse);

    // loadStripe is idempotent on the same publishableKey + options pair.
    let stripe: Stripe | null;
    try {
      stripe = await loadStripe(publishableKey, {
        stripeAccount: payResponse.stripe_account_id,
      });
    } catch (_err) {
      fail('Could not load Stripe. Please refresh and try again.');
      return;
    }
    if (!stripe) {
      fail('Stripe failed to initialise. Please refresh and try again.');
      return;
    }
    stripeRef.current = stripe;

    // Hand control to React so the mount target div is in the DOM
    // before we call .mount(). We do this by transitioning the phase
    // first, then mounting in an effect below.
    setPhase('element_ready');
  }, [bookingId, fail, phase, publishableKey]);

  // Mount the PaymentElement once the panel has rendered the target
  // div. Re-mounts are rare in practice but the cleanup unmounts so
  // a re-entry doesn't double-attach.
  useEffect(() => {
    if (phase !== 'element_ready') return;
    if (!intent || !stripeRef.current || !mountNodeRef.current) return;

    const elements = stripeRef.current.elements({
      clientSecret: intent.client_secret,
      appearance: { theme: 'stripe' },
    });
    elementsRef.current = elements;
    const paymentElement = elements.create('payment');
    paymentElement.mount(mountNodeRef.current);

    return () => {
      try {
        paymentElement.unmount();
      } catch {
        // ignore — happens if the element was already torn down.
      }
    };
  }, [intent, phase]);

  // Step 4+5: confirmPayment.
  const submitPayment = useCallback(async () => {
    if (!stripeRef.current || !elementsRef.current) {
      fail('Payment not ready. Please try again.');
      return;
    }
    setPhase('submitting');
    setErrorMsg(null);

    const { error, paymentIntent } = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      // `redirect: 'if_required'` lets us avoid a hard redirect when
      // the payment method doesn't need it (cards usually don't); we
      // surface SCA via Stripe.js's internal modal handling.
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/account/bookings/${bookingId}`,
      },
    });

    if (error) {
      // `error.message` is human-readable — surface it directly.
      fail(error.message ?? 'Payment failed. Please try again.');
      return;
    }
    if (!paymentIntent) {
      fail('Payment did not complete. Please try again.');
      return;
    }

    setPhase('paid');
    onPaid();
  }, [bookingId, fail, onPaid]);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  if (phase === 'paid') {
    return (
      <div
        style={{
          marginTop: '1rem',
          padding: '1rem 1.25rem',
          borderRadius: '12px',
          border: '1px solid oklch(0.55 0.12 150 / 0.30)',
          background: 'oklch(0.55 0.12 150 / 0.08)',
          color: 'oklch(0.36 0.12 150)',
          fontSize: '0.9rem',
        }}
      >
        Payment received. Thank you.
      </div>
    );
  }

  const amountLabel = intent !== null ? formatMoney(intent.amount_minor, intent.currency) : null;

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginBottom: '0.75rem',
        }}
      >
        Payment
      </div>

      {phase === 'idle' && (
        <button
          type="button"
          onClick={beginPayment}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.85rem 1rem',
            borderRadius: '10px',
            border: 'none',
            background: 'var(--primary, oklch(0.43 0.18 263))',
            color: 'white',
            fontSize: '0.95rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Pay
        </button>
      )}

      {phase === 'creating_intent' && (
        <p
          style={{
            margin: 0,
            fontSize: '0.9rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Preparing payment…
        </p>
      )}

      {(phase === 'element_ready' || phase === 'submitting') && (
        <>
          <div ref={mountNodeRef} data-testid="payment-element" />
          <button
            type="button"
            onClick={submitPayment}
            disabled={phase === 'submitting'}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '1rem',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: 500,
              cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
              opacity: phase === 'submitting' ? 0.6 : 1,
            }}
          >
            {phase === 'submitting'
              ? 'Processing…'
              : amountLabel
                ? `Pay now ${amountLabel}`
                : 'Pay now'}
          </button>
        </>
      )}

      {phase === 'error' && (
        <>
          <p
            style={{
              margin: '0 0 0.75rem',
              color: 'var(--destructive, oklch(0.55 0.15 30))',
              fontSize: '0.9rem',
            }}
          >
            {errorMsg ?? 'Payment failed.'}
          </p>
          <button
            type="button"
            onClick={beginPayment}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}
